import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import AddressPicker, { AddressType } from '../Address/AddressPicker';
import Button from '../Button/Button';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Input, Label, Select as FormSelect } from '../Form';
import useNotify from '../../hooks/useNotify';
import Form from '../Form/Form';
import FormColumn from '../Form/FormColumn';
import FormRow from '../Form/FormRow';
import TagPicker from '../TagPicker/TagPicker';
import { PropertyType, PropertyTypePicker } from './PropertyTypePicker';
import { SourceType, SourceTypePicker } from './SourceTypePicker';
import { gql } from '@/modes/waterSupplier/gql';
import WaterSitePicker from '../WaterSite/WaterSitePicker';

export type WaterServiceInput = {
  id?: string;
  addressId?: string;
  meterNumber?: string;
  meterSize?: number;
  type?: PropertyType;
  sourceType?: SourceType;
  pin?: string;
  siteNumber?: string;
  radioId?: string;
  recycledWaterSiteId?: string | null;
  hazardSiteId?: string | null;
  tags?: string[];
};

type ModalColumnProps = {
  onSave?: (waterService: WaterServiceInput) => void;
  onClose?: () => void;
  waterServiceId?: string;
  readOnly?: boolean;
} & {
  waterService: WaterServiceInput;
};

const meterSizes = [
  { label: 'Select Size', value: 0 },
  { label: '5/8"', value: 0.625 },
  { label: '3/4"', value: 0.75 },
  { label: '1"', value: 1 },
  { label: '1 1/2"', value: 1.5 },
];

const UPDATE_WATER_SERVICE = gql(`
  mutation UpdateWaterService($id: ID!, $input: WaterServiceInput!) {
    updateWaterService(id: $id, input: $input) {
      id
      waterSupplierId
      siteNumber
      sourceType
      meterNumber
      meterSize
      radioId
      pin
      type
      recycledWaterSiteId
      hazardSiteId
      status
      tags
      addressId
      address {
        id
        street1
        street2
        city
        state
        zip
        zipExt
      }
      backflowAssemblies {
        id
        model {
          name
          backflowAssemblyMake {
            name
          }
        }
        serialNumber
        testAnniversary
        lastTestDate
        lastTest {
          jobId
          initialResult
          finalResult
        }
        lastNoticeSent {
          created
        }
        status
        testStatus
        location
        type
        hazardType
        hazardLevel
      }
      serviceAddresses {
        customerId
        customerRole
        customer {
          status
          profile {
            name
          }
          accountNumber
        }
        sendNotices
      }
      comments {
        id
        profile {
          id
          name
        }
        comment
        created
      }
    }
  }
`);
const WaterServiceEditModal: React.FC<ModalColumnProps> = ({
  onClose,
  onSave,
  waterService,
  readOnly = false,
}) => {
  const [waterServiceInput, setWaterServiceInput] = useState<WaterServiceInput>(
    {
      addressId: waterService.addressId,
      meterNumber: waterService.meterNumber,
      meterSize: waterService.meterSize ? waterService.meterSize : undefined,
      pin: waterService.pin,
      type: waterService.type!,
      sourceType: waterService.sourceType,
      siteNumber: waterService.siteNumber,
      radioId: waterService.radioId,
      recycledWaterSiteId: waterService.recycledWaterSiteId,
      hazardSiteId: waterService.hazardSiteId,
      tags: waterService.tags ?? [],
    }
  );

  const { showError, showSuccess } = useNotify();

  const handleChange = (e: any) => {
    let { value } = e.target;
    const { name } = e.target;
    if (name === 'meterSize') {
      value = parseFloat(value);
    }
    setWaterServiceInput((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleTagChange = (tags: readonly string[]) => {
    setWaterServiceInput((prevState) => ({
      ...prevState,
      tags: tags.slice(),
    }));
  };

  const updateAddress = (
    newAddress: AddressType | null,
    addressType: string
  ) => {
    setWaterServiceInput((prevState) => ({
      ...prevState,
      [addressType]: newAddress?.id ?? '',
    }));
  };

  const [
    updateWaterService,
    { data: updateData, loading: updateLoading, error: updateError },
  ] = useMutation<{ updateWaterService: WaterServiceInput }>(
    UPDATE_WATER_SERVICE,
    {
      onError: (error) => {
        console.error('Error updating customer', error);
      },
    }
  );

  const save = useCallback(async () => {
    updateWaterService({
      variables: { id: waterService?.id, input: waterServiceInput },
      onCompleted: (data) => {
        if (data.updateWaterService) {
          onSave?.(data.updateWaterService);
          showSuccess('Water service updated successfully');
        }
        onClose?.();
      },
      onError: (error) => {
        console.error('Error saving water service', error);
        showError('Error saving water service', error);
      },
    });
  }, [waterService.id, waterServiceInput, updateWaterService]);

  return (
    <Modal onClose={onClose} title="Edit Water Service">
      <ModalBody>
        <Form>
          <FormColumn>
            <Label>Address</Label>
            <AddressPicker
              addressId={waterServiceInput?.addressId}
              onChange={(newAddress) => updateAddress(newAddress, 'addressId')}
              allowCreate
              isDisabled={readOnly}
            />
          </FormColumn>
          <FormRow>
            <FormColumn>
              <Label>Service Number</Label>
              <Input
                width="100%"
                type="text"
                value={waterServiceInput.siteNumber}
                name="siteNumber"
                onChange={handleChange}
                autoComplete="off"
                disabled={readOnly}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Property Type</Label>
              <PropertyTypePicker
                isMulti={false}
                value={waterServiceInput.type}
                isDisabled={readOnly}
                onChange={(value) => {
                  setWaterServiceInput((prevState) => ({
                    ...prevState,
                    type: value,
                  }));
                }}
              />
            </FormColumn>
            <FormColumn>
              <Label>Source Type</Label>
              <SourceTypePicker
                isMulti={false}
                value={waterServiceInput.sourceType}
                isDisabled={readOnly}
                onChange={(value) => {
                  setWaterServiceInput((prevState) => ({
                    ...prevState,
                    sourceType: value,
                  }));
                }}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Meter Number</Label>
              <Input
                width="100%"
                type="text"
                value={waterServiceInput.meterNumber}
                name="meterNumber"
                onChange={handleChange}
                autoComplete="off"
                disabled={readOnly}
              />
            </FormColumn>
            <FormColumn>
              <Label>Meter Size</Label>
              <FormSelect
                flex={1}
                value={waterServiceInput.meterSize}
                name="meterSize"
                onChange={handleChange}
                disabled={readOnly}
              >
                {meterSizes.map((size) => (
                  <option key={size.label} value={size.value}>
                    {size.label}
                  </option>
                ))}
              </FormSelect>
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>TRAC</Label>
              <Input
                width="100%"
                type="text"
                value={waterServiceInput.pin}
                name="pin"
                onChange={handleChange}
                autoComplete="off"
                disabled={readOnly}
              />
            </FormColumn>
            <FormColumn>
              <Label>Radio ID</Label>
              <Input
                width="100%"
                type="text"
                value={waterServiceInput.radioId}
                name="radioId"
                onChange={handleChange}
                autoComplete="off"
                disabled={readOnly}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Recycled Water Site</Label>
              <WaterSitePicker
                siteType="RECYCLED"
                waterSiteId={waterServiceInput.recycledWaterSiteId}
                isClearable
                onChange={(site) => {
                  setWaterServiceInput((prevState) => ({
                    ...prevState,
                    recycledWaterSiteId: site?.id ?? null,
                  }));
                }}
              />
            </FormColumn>
            <FormColumn>
              <Label>Hazard Site</Label>
              <WaterSitePicker
                siteType="HAZARD"
                waterSiteId={waterServiceInput.hazardSiteId}
                isClearable
                onChange={(site) => {
                  setWaterServiceInput((prevState) => ({
                    ...prevState,
                    hazardSiteId: site?.id ?? null,
                  }));
                }}
              />
            </FormColumn>
          </FormRow>
          <FormRow>
            <FormColumn>
              <Label>Tags</Label>
              <TagPicker
                value={waterServiceInput.tags}
                onChange={handleTagChange}
                isDisabled={readOnly}
              />
            </FormColumn>
          </FormRow>
        </Form>
      </ModalBody>
      <ModalFooter>
        {!readOnly && (
          <Button
            icon="save"
            disabled={
              !waterServiceInput.siteNumber || !waterServiceInput.addressId
            }
            onClick={save}
          >
            Save
          </Button>
        )}
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default WaterServiceEditModal;
